import type { ApiErrorCode, ProblemDetails } from './types';

/**
 * Every failed call rejects with an ApiError. `code` is the stable machine code from problem+json
 * (`type`), which is what UI code should branch on — never on the human-readable title.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly title: string;
  readonly errors?: Record<string, string[]>;
  readonly problem?: ProblemDetails;
  readonly traceId?: string;

  constructor(init: {
    status: number;
    code: ApiErrorCode;
    title: string;
    errors?: Record<string, string[]>;
    problem?: ProblemDetails;
    traceId?: string;
  }) {
    super(init.title);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.errors = init.errors;
    this.problem = init.problem;
    this.traceId = init.traceId;
  }

  /** True when the browser could not reach the API at all. */
  get isNetwork(): boolean {
    return this.status === 0;
  }

  /** First validation message for a field, if the API returned one. */
  fieldError(field: string): string | undefined {
    if (!this.errors) return undefined;
    const key = Object.keys(this.errors).find((k) => k.toLowerCase() === field.toLowerCase());
    return key ? this.errors[key]?.[0] : undefined;
  }

  static fromProblem(status: number, problem: ProblemDetails): ApiError {
    return new ApiError({
      status,
      code: (problem.type as ApiErrorCode) || fallbackCode(status),
      title: problem.title || problem.detail || `Request failed (${status})`,
      errors: problem.errors,
      problem,
      traceId: typeof problem.traceId === 'string' ? problem.traceId : undefined,
    });
  }

  static network(message: string): ApiError {
    return new ApiError({ status: 0, code: 'network_error', title: message });
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/**
 * "The demo account may not do that" — a 403 whose problem code is `demo_read_only`.
 *
 * The client announces these on its own channel (`onDemoReadOnly`) and the app turns that into one calm
 * toast, so a page's own error handling must stay quiet about them: a second, red "something went wrong on
 * our side" under the first is both a duplicate and a lie. Every `onError` that would otherwise speak
 * checks this first.
 */
export function isDemoReadOnly(e: unknown): boolean {
  return isApiError(e) && e.status === 403 && e.code === 'demo_read_only';
}

function fallbackCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'validation_error';
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 429:
      return 'rate_limited';
    default:
      return status >= 500 ? 'internal_error' : 'validation_error';
  }
}
